"use client";

import Section from "@/components/static/Section";
import { Fragment, useRef, useState } from "react";
import { motion, MotionValue, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import Topline from "@/components/static/Topline";
import Buttons from "@/components/static/Buttons";
import Button from "@/components/static/Button";

type Content = {
    topline: string;
    text: string;
    buttons: [
        {
            link: {
                title: string;
                url: string;
                target: string;
            };
            type: string;
        }
    ];
    settings: {
        padding: {
            bottom: string;
            top: string;
        };
    };
};

export default function Layout_484({ content }: { content: Content }) {
    const component = useRef<HTMLDivElement>(null);

    const { scrollYProgress } = useScroll({
        target: component,
        offset: ["start start", "end end"],
    });

    const words = content?.text?.split(" ");

    const [buttonsVisible, setButtonsVisible] = useState(false);

    useMotionValueEvent(scrollYProgress, "change", (value) => {
        setButtonsVisible(value === 1);
    });

    return (
        <Section dataComponent='Layout_484' preventAnimation={true} settings={content.settings}>
            <div className='container' ref={component}>
                <div className='h-[250vh]'>
                    <div className='h-[100vh] sticky top-0 flex items-center'>
                        <div className='lg:max-w-lg mx-auto'>
                            {content?.topline && <Topline>{content?.topline}</Topline>}
                            <div className='text-center text-[32px] md:text-h2 leading-[110%] text-brandDark'>
                                {words?.map((word: string, index: number) => {
                                    return (
                                        <Fragment key={index}>
                                            <Word
                                                length={words.length}
                                                scrollYProgress={scrollYProgress}
                                                index={index}
                                                key={index}
                                            >
                                                {word}
                                            </Word>{" "}
                                        </Fragment>
                                    );
                                })}
                            </div>
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{
                                    opacity: buttonsVisible ? 1 : 0,
                                    y: buttonsVisible ? 0 : 50,
                                }}
                                className='mt-12'
                            >
                                <Buttons className='buttons gap-x-8'>
                                    {content?.buttons.length &&
                                        content?.buttons?.map((button, index) => {
                                            return (
                                                <Button
                                                    as='link'
                                                    href={button?.link?.url}
                                                    type={button?.type}
                                                    key={index}
                                                >
                                                    {button?.link?.title}
                                                </Button>
                                            );
                                        })}
                                </Buttons>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </Section>
    );
}

function Word({
    children,
    index,
    length,
    scrollYProgress,
}: {
    children: React.ReactNode;
    index: number;
    length: number;
    scrollYProgress: MotionValue;
}) {
    const start = (index * 1) / length;
    const end = start + 1 / length;
    const opacity = useTransform(scrollYProgress, [start, end], [0.1, 1]);

    return (
        <motion.span className='inline-block' style={{ opacity }} key={index}>
            {children}
        </motion.span>
    );
}
